/**
 * @fileoverview Lines of Code Report Page
 * @description Main page — search repos via dialog, submit LOC analysis jobs,
 *              track progress in real-time, view/download reports.
 *              Uses MUI components + Alaska Airlines design system.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Paper,
  Chip,
  IconButton,
  LinearProgress,
  Tooltip,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Database,
  Search,
  Play,
  Download,
  Eye,
  RefreshCw,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader,
  FileSpreadsheet,
  GitBranch,
  Plus,
  Layers,
  Edit3,
  Trash2,
} from 'lucide-react';
import { linesOfCodeApi } from '../services/linesOfCodeApi';
import {appInsights} from '@/telemetry';
import './LinesOfCode.css';

// ── Days options ────────────────────────────────────────────
const DAYS_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

// ── Status helpers ──────────────────────────────────────────
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#F5A623', icon: Clock, bg: '#FEF3C7' },
  running: { label: 'Running', color: '#0097A9', icon: Loader, bg: '#ECFEFF' },
  completed: { label: 'Completed', color: '#00AF66', icon: CheckCircle, bg: '#ECFDF5' },
  failed: { label: 'Failed', color: '#E31837', icon: AlertCircle, bg: '#FEF2F2' },
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Chip
      icon={<Icon size={14} />}
      label={config.label}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.75rem',
        background: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
        '& .MuiChip-icon': { color: config.color },
      }}
    />
  );
};

// ── Main Component ──────────────────────────────────────────
export const LinesOfCode = () => {
  // State
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [daysBack, setDaysBack] = useState(30);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const pollingRef = useRef(null);

  // Search dialog state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dialogSelected, setDialogSelected] = useState([]);
  const [allReposLoading, setAllReposLoading] = useState(false);

  // Suite state
  const [suites, setSuites] = useState([]);
  const [suitesLoading, setSuitesLoading] = useState(true);
  const [suiteDialogOpen, setSuiteDialogOpen] = useState(false);
  const [editingSuite, setEditingSuite] = useState(null);
  const [suiteName, setSuiteName] = useState('');
  const [suiteDesc, setSuiteDesc] = useState('');
  const [suiteRepos, setSuiteRepos] = useState([]);
  const [suiteSearchQuery, setSuiteSearchQuery] = useState('');
  const [suiteSearchResults, setSuiteSearchResults] = useState([]);
  const [suiteSearchLoading, setSuiteSearchLoading] = useState(false);

  // ── Load suites ───────────────────────────────────────────
  const loadSuites = useCallback(async () => {
    try {
      const response = await linesOfCodeApi.fetchSuites();
      setSuites(response?.data?.suites || []);
    } catch (err) {
      console.error('Failed to load suites:', err);
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'loadSuites' }
      });
    } finally {
      setSuitesLoading(false);
    }
  }, []);

  // ── Load jobs on mount ────────────────────────────────────
  const loadJobs = useCallback(async () => {
    try {
      const response = await linesOfCodeApi.fetchJobs();
      setJobs(response?.data?.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'loadJobs' }
      });
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadSuites();
  }, [loadJobs, loadSuites]);

  // ── Poll for running jobs (every 5s for real-time progress) ─
  useEffect(() => {
    const hasActiveJobs = jobs.some((j) => j.status === 'running' || j.status === 'pending');
    if (hasActiveJobs) {
      pollingRef.current = setInterval(loadJobs, 5000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [jobs, loadJobs]);

  // ── Search Dialog Logic ───────────────────────────────────
  const handleSearchOpen = () => {
    setSearchOpen(true);
    setSearchQuery('');
    setSearchResults([]);
    setDialogSelected([]);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setDialogSelected([]);
  };

  const executeSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    setSearchLoading(true);
    try {
      const response = await linesOfCodeApi.searchRepositories(searchQuery);
      const repos = response?.data?.repositories || [];
      setSearchResults(repos);
    } catch (err) {
      console.error('Search error:', err);
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'executeSearch' }
      });
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') executeSearch();
  };

  const toggleDialogRepo = (repo) => {
    setDialogSelected((prev) => {
      const exists = prev.some((r) => r.org === repo.org && r.repo === repo.repo);
      if (exists) return prev.filter((r) => !(r.org === repo.org && r.repo === repo.repo));
      return [...prev, { org: repo.org, repo: repo.repo }];
    });
  };

  const isRepoSelected = (repo) => {
    return (
      dialogSelected.some((r) => r.org === repo.org && r.repo === repo.repo) ||
      selectedRepos.some((r) => r.org === repo.org && r.repo === repo.repo)
    );
  };

  const handleAddSelected = () => {
    // Merge dialog selections into main selected repos (no duplicates)
    setSelectedRepos((prev) => {
      const combined = [...prev];
      for (const repo of dialogSelected) {
        if (!combined.some((r) => r.org === repo.org && r.repo === repo.repo)) {
          combined.push(repo);
        }
      }
      return combined;
    });
    handleSearchClose();
  };

  // ── Remove repo from selection ────────────────────────────
  const removeRepo = (repo) => {
    setSelectedRepos((prev) => prev.filter((r) => !(r.org === repo.org && r.repo === repo.repo)));
  };

  // ── Select ALL org repos (inside dialog) ──────────────────
  const handleSelectAll = async () => {
    setAllReposLoading(true);
    try {
      const response = await linesOfCodeApi.fetchAllRepositories();
      const repos = response?.data?.repositories || [];
      if (repos.length === 0) {
        setError('No repositories found in the organization.');
      } else {
        setSelectedRepos(repos.map((r) => ({ org: r.org, repo: r.repo })));
        setSuccessMsg(`All ${repos.length} repositories selected.`);
        handleSearchClose();
      }
    } catch (err) {
      setError('Failed to fetch all repositories: ' + (err.message || 'Unknown error'));
    } finally {
      setAllReposLoading(false);
    }
  };

  // ── Load suite repos into selection (from dialog) ────────
  const handleSelectSuite = async (suiteId) => {
    try {
      const response = await linesOfCodeApi.fetchSuite(suiteId);
      const repos = response?.data?.repositories || [];
      if (repos.length > 0) {
        setSelectedRepos(repos);
        const suite = suites.find((s) => s.id === suiteId);
        setSuccessMsg(`Suite "${suite?.name}" loaded with ${repos.length} repositories.`);
        handleSearchClose();
      }
    } catch (err) {
      setError('Failed to load suite: ' + (err.message || 'Unknown error'));
    }
  };

  // ── Suite CRUD handlers ───────────────────────────────────
  const openSuiteDialog = (suite = null) => {
    if (suite) {
      setEditingSuite(suite);
      setSuiteName(suite.name || '');
      setSuiteDesc(suite.description || '');
      // Load repos for editing
      linesOfCodeApi.fetchSuite(suite.id).then((res) => {
        setSuiteRepos(res?.data?.repositories || []);
      }).catch((err) => {
        setError('Failed to load suite repositories: ' + (err.message || 'Unknown error'));
      });
    } else {
      setEditingSuite(null);
      setSuiteName('');
      setSuiteDesc('');
      setSuiteRepos([]);
    }
    setSuiteDialogOpen(true);
  };

  const closeSuiteDialog = () => {
    setSuiteDialogOpen(false);
    setEditingSuite(null);
    setSuiteName('');
    setSuiteDesc('');
    setSuiteRepos([]);
    setSuiteSearchQuery('');
    setSuiteSearchResults([]);
  };

  const handleSuiteSearch = async () => {
    if (suiteSearchQuery.length < 2) return;
    setSuiteSearchLoading(true);
    try {
      const response = await linesOfCodeApi.searchRepositories(suiteSearchQuery);
      setSuiteSearchResults(response?.data?.repositories || []);
    } catch (err) {
      console.error(err);
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'handleSuiteSearch' }
      });
    } finally {
      setSuiteSearchLoading(false);
    }
  };

  const addRepoToSuite = (repo) => {
    setSuiteRepos((prev) => {
      if (prev.some((r) => r.org === repo.org && r.repo === repo.repo)) return prev;
      return [...prev, { org: repo.org, repo: repo.repo }];
    });
  };

  const removeRepoFromSuite = (repo) => {
    setSuiteRepos((prev) => prev.filter((r) => !(r.org === repo.org && r.repo === repo.repo)));
  };

  const handleSaveSuite = async () => {
    if (!suiteName.trim()) {
      setError('Suite name is required');
      return;
    }
    try {
      const payload = { name: suiteName.trim(), description: suiteDesc.trim(), repositories: suiteRepos };
      if (editingSuite) {
        await linesOfCodeApi.updateSuite(editingSuite.id, payload);
        setSuccessMsg(`Suite "${suiteName}" updated.`);
      } else {
        await linesOfCodeApi.createSuite(payload);
        setSuccessMsg(`Suite "${suiteName}" created.`);
      }
      closeSuiteDialog();
      loadSuites();
    } catch (err) {
      setError('Failed to save suite: ' + (err.message || 'Unknown error'));
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'handleSaveSuite' }
      });
    }
  };

  const handleDeleteSuite = async (suiteId) => {
    if (!window.confirm('Are you sure you want to delete this suite?')) return;
    try {
      await linesOfCodeApi.deleteSuite(suiteId);
      setSuccessMsg('Suite deleted.');
      loadSuites();
    } catch (err) {
      setError('Failed to delete suite: ' + (err.message || 'Unknown error'));
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'handleDeleteSuite' }
      });
    }
  };

  // ── Submit job ────────────────────────────────────────────
  const handleSubmit = async () => {
    if (selectedRepos.length === 0) {
      setError('Please add at least one repository');
      return;
    }
    setError(null);
    setSuccessMsg(null);
    setSubmitLoading(true);
    try {
      const response = await linesOfCodeApi.submitJob({
        repositories: selectedRepos,
        days_back: daysBack,
      });
      const data = response?.data;
      if (data?.cached) {
        setSuccessMsg('A report with the same configuration was already generated today. Using cached results.');
      } else {
        setSuccessMsg('Report generation started! You will be notified when it completes.');
      }
      setSelectedRepos([]);
      await loadJobs();
    } catch (err) {
      setError(err.message || 'Failed to submit job');
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'handleSubmit' }
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // ── View report ───────────────────────────────────────────
  const handleViewReport = async (jobId) => {
    setReportLoading(true);
    setSelectedReport(null);
    try {
      const response = await linesOfCodeApi.fetchReport(jobId);
      setSelectedReport({ jobId, ...(response?.data || response) });
    } catch (err) {
      setError('Failed to load report data: ' + (err.message || 'Unknown error'));
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'handleViewReport', jobId }
      });
    } finally {
      setReportLoading(false);
    }
  };

  // ── Download report ───────────────────────────────────────
  const handleDownload = (jobId) => {
    try {
      const url = linesOfCodeApi.getDownloadUrl(jobId);
      window.open(url, '_blank');
    } catch (err) {
      setError('Failed to download report: ' + (err.message || 'Unknown error'));
      appInsights.trackException({
        exception: err,
        properties: { source: 'LinesOfCode', action: 'handleDownload', jobId }
      });
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <Box className="loc-page" sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Database size={28} color="#01426A" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#01426A' }}>
              Lines of Code Report
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Analyze GitHub repositories for code metrics and commit activity
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshCw size={14} />}
          onClick={loadJobs}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Alerts */}
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>
      <Collapse in={!!successMsg}>
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      </Collapse>

      {/* ── Generate Report Section ──────────────────────── */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #E5E7EB' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A', mb: 2 }}>
          Generate New Report
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          {/* Suite quick-select (if suites exist) */}
          {suites.length > 0 && (
            <TextField
              select
              size="small"
              label="Select Suite"
              value=""
              onChange={(e) => {
                const id = Number(e.target.value);
                if (id) handleSelectSuite(id);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" disabled>
                <em>Choose a suite...</em>
              </MenuItem>
              {suites.map((suite) => (
                <MenuItem key={suite.id} value={suite.id}>
                  {suite.name} ({suite.repo_count} repos)
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Or search manually */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Search size={14} />}
            onClick={handleSearchOpen}
            sx={{
              textTransform: 'none', fontWeight: 600, borderColor: '#01426A',
              color: '#01426A', '&:hover': { borderColor: '#0097A9', color: '#0097A9' },
            }}
          >
            Custom Selection
          </Button>

          {/* Days back selector */}
          <TextField
            select
            size="small"
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            sx={{ minWidth: 140 }}
            label="Days Back"
          >
            {DAYS_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                {d} days
              </MenuItem>
            ))}
          </TextField>

          {/* Submit button */}
          <Button
            variant="contained"
            startIcon={submitLoading ? <CircularProgress size={14} color="inherit" /> : <Play size={14} />}
            onClick={handleSubmit}
            disabled={submitLoading || selectedRepos.length === 0}
            sx={{
              textTransform: 'none', fontWeight: 600, minWidth: 160,
              backgroundColor: '#01426A', '&:hover': { backgroundColor: '#002F5F' },
            }}
          >
            {submitLoading ? 'Submitting...' : 'Generate Report'}
          </Button>
        </Stack>

        {/* Selected repos summary (collapsible) */}
        {selectedRepos.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={`${selectedRepos.length} repositories selected`}
                size="small"
                sx={{ fontWeight: 600, background: '#01426A', color: 'white' }}
              />
              <Button
                size="small"
                onClick={() => setSelectedRepos([])}
                sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#E31837' }}
              >
                Clear All
              </Button>
              <Button
                size="small"
                onClick={() => document.getElementById('loc-repo-details')?.toggleAttribute('open')}
                sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6B7280' }}
              >
                View / Edit
              </Button>
            </Stack>
            <details id="loc-repo-details" style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#6B7280', marginBottom: 6 }}>
                Show all {selectedRepos.length} repositories
              </summary>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 160, overflow: 'auto' }}>
                {selectedRepos.map((repo) => (
                  <Chip
                    key={`${repo.org}/${repo.repo}`}
                    label={repo.repo}
                    onDelete={() => removeRepo(repo)}
                    deleteIcon={<X size={10} />}
                    size="small"
                    sx={{
                      fontFamily: 'monospace', fontSize: '0.7rem',
                      background: '#EFF6FF', color: '#01426A', border: '1px solid #BFDBFE',
                    }}
                  />
                ))}
              </Box>
            </details>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 1, display: 'block' }}>
            Select a suite or click "Custom Selection" to pick repositories.
          </Typography>
        )}
      </Paper>

      {/* ── Active/Recent Jobs Section ───────────────────── */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #E5E7EB' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A', mb: 2 }}>
          Report Jobs
        </Typography>

        {jobsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: '#9CA3AF' }}>
            <FileSpreadsheet size={40} style={{ marginBottom: 8, opacity: 0.5 }} />
            <Typography variant="body2">
              No report jobs yet. Search and select repositories above to generate a report.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, color: '#6B7280', fontSize: '0.8rem' } }}>
                  <TableCell>Job</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Repos</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => {
                  const progressPct = job.total_repos > 0
                    ? Math.round((job.processed_repos / job.total_repos) * 100)
                    : 0;
                  return (
                    <TableRow key={job.job_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {job.job_id.slice(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={job.status} />
                      </TableCell>
                      <TableCell>{job.total_repos}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant={job.status === 'running' && progressPct === 0 ? 'indeterminate' : 'determinate'}
                            value={progressPct}
                            sx={{
                              flex: 1, height: 8, borderRadius: 4,
                              backgroundColor: '#E5E7EB',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: job.status === 'completed' ? '#00AF66' : '#0097A9',
                                borderRadius: 4,
                                transition: 'transform 0.5s ease',
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#6B7280', minWidth: 55, fontWeight: 600 }}>
                            {job.processed_repos}/{job.total_repos} ({progressPct}%)
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{job.days_back}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                          {new Date(job.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {job.status === 'completed' && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="View Report">
                              <IconButton size="small" onClick={() => handleViewReport(job.job_id)}>
                                <Eye size={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Excel">
                              <IconButton size="small" onClick={() => handleDownload(job.job_id)} color="primary">
                                <Download size={16} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                        {(job.status === 'running' || job.status === 'pending') && (
                          <Tooltip title="Processing...">
                            <CircularProgress size={18} sx={{ color: '#0097A9' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Report Viewer Section ────────────────────────── */}
      {(reportLoading || selectedReport) && (
        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, border: '1px solid #E5E7EB' }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A' }}>
              Report Results
            </Typography>
            {selectedReport && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  Generated: {new Date(selectedReport.generated_at).toLocaleString()}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Download size={14} />}
                  onClick={() => handleDownload(selectedReport.jobId)}
                  sx={{
                    textTransform: 'none', fontWeight: 600,
                    backgroundColor: '#00AF66', '&:hover': { backgroundColor: '#009956' },
                  }}
                >
                  Download Excel
                </Button>
                <IconButton size="small" onClick={() => setSelectedReport(null)}>
                  <X size={16} />
                </IconButton>
              </Stack>
            )}
          </Stack>

          {reportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : selectedReport?.data ? (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, background: '#F9FAFB', color: '#01426A' } }}>
                    <TableCell>Organization</TableCell>
                    <TableCell>Repository</TableCell>
                    <TableCell align="right">Branches</TableCell>
                    <TableCell align="right">Total LOC</TableCell>
                    <TableCell align="right">Lines Added</TableCell>
                    <TableCell align="right">Lines Deleted</TableCell>
                    <TableCell align="right">Lines Edited</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedReport.data || []).map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{row.org}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {row.repository}
                      </TableCell>
                      <TableCell align="right">{row.branches_count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {(row.total_loc || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#00AF66', fontWeight: 500 }}>
                        +{(row.total_lines_added || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#E31837', fontWeight: 500 }}>
                        -{(row.total_lines_deleted || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#0097A9', fontWeight: 500 }}>
                        {(row.total_lines_edited || 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Paper>
      )}

      {/* ── Suite Management Section ─────────────────────── */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #E5E7EB' }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Layers size={20} color="#4C1D95" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A' }}>
              Repository Suites
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={14} />}
            onClick={() => openSuiteDialog()}
            sx={{
              textTransform: 'none', fontWeight: 600,
              backgroundColor: '#4C1D95', '&:hover': { backgroundColor: '#3B0764' },
            }}
          >
            Create Suite
          </Button>
        </Stack>

        <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
          Organize repositories into suites for quick selection when generating reports.
        </Typography>

        {suitesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : suites.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, color: '#9CA3AF' }}>
            <Layers size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <Typography variant="body2">No suites created yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, color: '#6B7280', fontSize: '0.8rem' } }}>
                  <TableCell>Suite Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Repos</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suites.map((suite) => (
                  <TableRow key={suite.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#01426A' }}>
                        {suite.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        {suite.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={suite.repo_count}
                        size="small"
                        sx={{ fontWeight: 600, background: '#EDE9FE', color: '#6D28D9' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        {suite.created_by}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit Suite">
                          <IconButton size="small" onClick={() => openSuiteDialog(suite)}>
                            <Edit3 size={15} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Suite">
                          <IconButton size="small" color="error" onClick={() => handleDeleteSuite(suite.id)}>
                            <Trash2 size={15} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Search & Add Repositories Dialog ─────────────── */}
      <Dialog
        open={searchOpen}
        onClose={handleSearchClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#01426A' }}>
              Add Repositories
            </Typography>
            <IconButton size="small" onClick={handleSearchClose}>
              <X size={18} />
            </IconButton>
          </Stack>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            Pick a suite, search specific repos, or select all in the organization
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Quick Action: Select All */}
          <Box
            sx={{
              p: 2, background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
              borderBottom: '1px solid #BAE6FD',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#01426A' }}>
                  All Organization Repositories
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  Select all 3,000+ repos in Alaska-ITS at once
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={allReposLoading ? <CircularProgress size={14} color="inherit" /> : <Database size={14} />}
                onClick={handleSelectAll}
                disabled={allReposLoading}
                sx={{
                  textTransform: 'none', fontWeight: 600, px: 2.5,
                  backgroundColor: '#0097A9', '&:hover': { backgroundColor: '#007A8A' },
                }}
              >
                {allReposLoading ? 'Fetching all repos...' : 'Select All'}
              </Button>
            </Stack>
          </Box>

          {/* Divider with OR */}
          <Box sx={{ position: 'relative', py: 1.5, px: 2 }}>
            <Divider />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff', px: 2, color: '#9CA3AF', fontWeight: 600,
              }}
            >
              OR search specific repos
            </Typography>
          </Box>

          {/* Search bar */}
          <Box sx={{ px: 2, pb: 2, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type repository name and press Enter or click Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GitBranch size={16} color="#9CA3AF" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={executeSearch}
                disabled={searchLoading || searchQuery.length < 2}
                sx={{
                  minWidth: 100, textTransform: 'none', fontWeight: 600,
                  backgroundColor: '#01426A', '&:hover': { backgroundColor: '#002F5F' },
                }}
              >
                {searchLoading ? <CircularProgress size={18} color="inherit" /> : 'Search'}
              </Button>
            </Stack>
          </Box>

          {/* Results */}
          <Box sx={{ minHeight: 200 }}>
            {searchLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!searchLoading && searchResults.length === 0 && searchQuery.length >= 2 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#9CA3AF' }}>
                <Search size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <Typography variant="body2">
                  No repositories found. Try a different search term.
                </Typography>
              </Box>
            )}

            {!searchLoading && searchResults.length === 0 && searchQuery.length < 2 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#9CA3AF' }}>
                <Search size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <Typography variant="body2">
                  Enter at least 2 characters and click Search.
                </Typography>
              </Box>
            )}

            {!searchLoading && searchResults.length > 0 && (
              <List dense disablePadding>
                {searchResults.map((repo) => {
                  const checked = isRepoSelected(repo);
                  const alreadyAdded = selectedRepos.some(
                    (r) => r.org === repo.org && r.repo === repo.repo
                  );
                  return (
                    <ListItem
                      key={`${repo.org}/${repo.repo}`}
                      disablePadding
                      secondaryAction={
                        alreadyAdded ? (
                          <Chip label="Already added" size="small" sx={{ fontSize: '0.7rem' }} />
                        ) : null
                      }
                    >
                      <ListItemButton
                        onClick={() => !alreadyAdded && toggleDialogRepo(repo)}
                        disabled={alreadyAdded}
                        sx={{ px: 2, py: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            disabled={alreadyAdded}
                            size="small"
                            sx={{ '&.Mui-checked': { color: '#01426A' } }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#1F2937' }}
                            >
                              {repo.org}/{repo.repo}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Dialog selected chips */}
          {dialogSelected.length > 0 && (
            <Box sx={{ p: 2, borderTop: '1px solid #E5E7EB', background: '#F9FAFB' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#01426A', mb: 1, display: 'block' }}>
                Selected in this search ({dialogSelected.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {dialogSelected.map((repo) => (
                  <Chip
                    key={`${repo.org}/${repo.repo}`}
                    label={repo.repo}
                    size="small"
                    onDelete={() => toggleDialogRepo(repo)}
                    deleteIcon={<X size={12} />}
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', background: '#EFF6FF', border: '1px solid #BFDBFE' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleSearchClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSelected}
            disabled={dialogSelected.length === 0}
            startIcon={<Plus size={14} />}
            sx={{
              textTransform: 'none', fontWeight: 600,
              backgroundColor: '#01426A', '&:hover': { backgroundColor: '#002F5F' },
            }}
          >
            Add {dialogSelected.length} Repository{dialogSelected.length !== 1 ? 'ies' : 'y'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Suite Create/Edit Dialog ─────────────────────── */}
      <Dialog
        open={suiteDialogOpen}
        onClose={closeSuiteDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#4C1D95' }}>
              {editingSuite ? 'Edit Suite' : 'Create New Suite'}
            </Typography>
            <IconButton size="small" onClick={closeSuiteDialog}>
              <X size={18} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* Name & Description */}
            <TextField
              fullWidth
              size="small"
              label="Suite Name"
              value={suiteName}
              onChange={(e) => setSuiteName(e.target.value)}
              placeholder="e.g., Alaska QE"
            />
            <TextField
              fullWidth
              size="small"
              label="Description (optional)"
              value={suiteDesc}
              onChange={(e) => setSuiteDesc(e.target.value)}
              placeholder="Brief description of this suite"
            />

            {/* Add repos via search */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#01426A', mb: 1 }}>
                Repositories ({suiteRepos.length})
              </Typography>
              <Stack direction="row" spacing={1} mb={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search repos to add..."
                  value={suiteSearchQuery}
                  onChange={(e) => setSuiteSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSuiteSearch()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={14} color="#9CA3AF" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSuiteSearch}
                  disabled={suiteSearchLoading || suiteSearchQuery.length < 2}
                  sx={{ textTransform: 'none', minWidth: 80 }}
                >
                  {suiteSearchLoading ? <CircularProgress size={16} /> : 'Search'}
                </Button>
              </Stack>

              {/* Search results */}
              {suiteSearchResults.length > 0 && (
                <Paper variant="outlined" sx={{ maxHeight: 150, overflow: 'auto', mb: 1.5 }}>
                  <List dense disablePadding>
                    {suiteSearchResults.map((repo) => {
                      const alreadyIn = suiteRepos.some((r) => r.org === repo.org && r.repo === repo.repo);
                      return (
                        <ListItemButton
                          key={`${repo.org}/${repo.repo}`}
                          onClick={() => !alreadyIn && addRepoToSuite(repo)}
                          disabled={alreadyIn}
                          sx={{ py: 0.5 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {repo.org}/{repo.repo}
                              </Typography>
                            }
                          />
                          {alreadyIn ? (
                            <Chip label="Added" size="small" sx={{ fontSize: '0.65rem' }} />
                          ) : (
                            <Plus size={14} color="#00AF66" />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Paper>
              )}

              {/* Current repos in suite */}
              {suiteRepos.length > 0 ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 2, p: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {suiteRepos.map((repo) => (
                      <Chip
                        key={`${repo.org}/${repo.repo}`}
                        label={`${repo.org}/${repo.repo}`}
                        size="small"
                        onDelete={() => removeRepoFromSuite(repo)}
                        deleteIcon={<X size={10} />}
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem', background: '#F5F3FF', border: '1px solid #DDD6FE' }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  No repositories added to this suite yet. Use the search above.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeSuiteDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSuite}
            disabled={!suiteName.trim()}
            sx={{
              textTransform: 'none', fontWeight: 600,
              backgroundColor: '#4C1D95', '&:hover': { backgroundColor: '#3B0764' },
            }}
          >
            {editingSuite ? 'Update Suite' : 'Create Suite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinesOfCode;
